import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Users, Trash2, Crown, BarChart3, RefreshCw, Upload, Mail,
  FileSpreadsheet, RotateCcw, CheckCircle, AlertCircle, Clock,
  Download, Copy, ExternalLink, ChevronDown, Eye, MousePointer
} from 'lucide-react';
import { toast } from 'sonner';

export default function LeadsPage() {
  const { api } = useAuth();
  const [leads, setLeads] = useState([]);
  const [stats, setStats] = useState({});
  const [trackingStats, setTrackingStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [batchProgress, setBatchProgress] = useState(null);
  const [showUrlImport, setShowUrlImport] = useState(false);
  const [importUrl, setImportUrl] = useState('');
  const [showStratoGuide, setShowStratoGuide] = useState(false);
  const [tab, setTab] = useState('overzicht');
  const fileRef = useRef(null);
  const pollRef = useRef(null);

  const fetchLeads = useCallback(async () => {
    try {
      const r = await api('get', '/api/admin/leads');
      setLeads(r.data.leads || []);
      setStats(r.data.stats || {});
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, [api]);

  const fetchTrackingStats = useCallback(async () => {
    try {
      const r = await api('get', '/api/admin/leads/stats');
      setTrackingStats(r.data);
    } catch (e) { console.error(e); }
  }, [api]);

  useEffect(() => { fetchLeads(); fetchTrackingStats(); }, [fetchLeads, fetchTrackingStats]);
  useEffect(() => {
    const interval = setInterval(() => { fetchTrackingStats(); fetchLeads(); }, 30000);
    return () => { clearInterval(interval); if (pollRef.current) clearInterval(pollRef.current); };
  }, [fetchTrackingStats, fetchLeads]);

  const startPolling = (batchId) => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      try {
        const r = await api('get', `/api/admin/leads/batch/${batchId}/progress`);
        const p = r.data;
        setBatchProgress(p);
        if (p.status === 'complete') {
          clearInterval(pollRef.current);
          pollRef.current = null;
          toast.success(`Batch voltooid: ${p.sent} verstuurd, ${p.errors} fouten`);
          fetchLeads();
        }
      } catch { /* ignore */ }
    }, 3000);
  };

  const handleSuccess = (data) => {
    setUploadResult(data);
    toast.success(`${data.saved} leads opgeslagen. Emails worden verstuurd.`);
    fetchLeads();
    if (data.batch_id && data.saved > 0) {
      setBatchProgress({ total: data.saved, sent: 0, errors: 0, status: 'sending' });
      startPolling(data.batch_id);
    }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true); setUploadResult(null); setBatchProgress(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const r = await api('post', '/api/admin/leads/upload', formData);
      handleSuccess(r.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload mislukt.');
    } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ''; }
  };

  const handleUrlImport = async () => {
    if (!importUrl.trim()) return;
    setUploading(true); setUploadResult(null); setBatchProgress(null);
    try {
      const r = await api('post', '/api/admin/leads/import-url', { url: importUrl.trim() });
      handleSuccess(r.data);
      setImportUrl(''); setShowUrlImport(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'URL import mislukt');
    } finally { setUploading(false); }
  };

  const clearAllLeads = async () => {
    if (!window.confirm('ALLE leads verwijderen? Dit kan niet ongedaan worden.')) return;
    try {
      const r = await api('delete', '/api/admin/leads/clear');
      toast.success(`${r.data.deleted} leads verwijderd`);
      setUploadResult(null); setBatchProgress(null); fetchLeads(); fetchTrackingStats();
    } catch { toast.error('Verwijderen mislukt'); }
  };

  const startSending = async () => {
    try {
      const r = await api('post', '/api/admin/leads/start-sending');
      r.data.success ? toast.success(`Email worker gestart voor ${r.data.pending} leads`) : toast.info(r.data.message);
    } catch { toast.error('Kon email worker niet starten'); }
  };

  const resetErrors = async () => {
    try {
      const r = await api('post', '/api/admin/leads/reset-errors');
      toast.success(`${r.data.reset} fout-leads gereset naar pending`);
      fetchLeads();
    } catch { toast.error('Reset mislukt'); }
  };

  const exportCSV = async (status = 'all') => {
    try {
      const r = await api('get', `/api/admin/leads/export-csv?status=${status}`, null, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement('a'); a.href = url;
      a.download = `leads_${status}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); window.URL.revokeObjectURL(url);
    } catch { toast.error('Export mislukt'); }
  };

  const copyEmailTemplate = async () => {
    try {
      const r = await api('get', '/api/admin/leads/email-template');
      await navigator.clipboard.writeText(r.data.html);
      toast.success('HTML template gekopieerd!');
    } catch { toast.error('Kopiëren mislukt'); }
  };

  const resendEmail = async (leadId) => {
    try {
      await api('post', `/api/admin/leads/${leadId}/resend`);
      toast.success('Email opnieuw verstuurd'); fetchLeads();
    } catch { toast.error('Opnieuw versturen mislukt'); }
  };

  const SC = {
    sent: { label: 'Verstuurd', color: '#22c55e', icon: CheckCircle },
    pending: { label: 'Wacht', color: '#d97706', icon: Clock },
    error: { label: 'Fout', color: '#ef4444', icon: AlertCircle },
    converted: { label: 'Geconverteerd', color: '#2563eb', icon: Crown },
  };

  const ts = trackingStats;

  return (
    <div style={{ maxWidth: '1200px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '24px' }}>
        <h1 style={{ fontFamily: "'Sora', sans-serif", fontSize: '24px', fontWeight: 800, color: '#0f172a', margin: '0 0 4px' }}>
          Leads & Email Campagnes
        </h1>
        <p style={{ fontSize: '13px', color: '#64748b', margin: 0 }}>
          Upload leads, verstuur campagnes en volg de resultaten in real-time.
        </p>
      </div>

      {/* KPI Cards */}
      <div className="leads-kpi-5" style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px', marginBottom: '20px' }}>
        {[
          { label: 'Totaal', value: stats.total || 0, color: '#0f172a', bg: '#f8fafc' },
          { label: 'Verstuurd', value: stats.sent || 0, color: '#22c55e', bg: '#f0fdf4' },
          { label: 'Afwachting', value: stats.pending || 0, color: '#d97706', bg: '#fffbeb' },
          { label: 'Geopend', value: ts?.totals?.opened || 0, color: '#818cf8', bg: '#f5f3ff' },
          { label: 'Geklikt', value: ts?.totals?.clicked || 0, color: '#2dd4bf', bg: '#f0fdfa' },
        ].map(s => (
          <div key={s.label} style={{
            background: s.bg, borderRadius: '12px', padding: '16px', textAlign: 'center',
            border: `1px solid ${s.color}15`,
          }}>
            <p style={{ fontSize: '26px', fontWeight: 900, color: s.color, margin: 0 }}>{s.value}</p>
            <p style={{ fontSize: '10px', color: '#94a3b8', margin: '4px 0 0', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>{s.label}</p>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: '2px', marginBottom: '20px', borderBottom: '2px solid #f1f5f9', overflowX: 'auto' }}>
        {[
          { id: 'overzicht', label: 'Overzicht & Upload', icon: Upload },
          { id: 'tracking', label: 'Tracking & Statistieken', icon: BarChart3 },
          { id: 'tabel', label: 'Alle Leads', icon: Users },
        ].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)} data-testid={`leads-tab-${t.id}`}
            style={{
              display: 'flex', alignItems: 'center', gap: '6px', padding: '10px 16px', fontSize: '13px',
              fontWeight: tab === t.id ? 700 : 500, color: tab === t.id ? '#D4A017' : '#64748b',
              background: 'none', border: 'none', cursor: 'pointer',
              borderBottom: `2px solid ${tab === t.id ? '#D4A017' : 'transparent'}`, marginBottom: '-2px',
            }}>
            <t.icon size={15} />{t.label}
          </button>
        ))}
      </div>

      {/* ── Tab: Overzicht & Upload ── */}
      {tab === 'overzicht' && (
        <>
          {/* Upload Section */}
          <div style={{
            background: 'linear-gradient(135deg, #0f172a, #1e1b4b)', borderRadius: '14px',
            padding: '24px', marginBottom: '20px', color: 'white',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '14px' }}>
              <div>
                <h3 style={{ fontSize: '16px', fontWeight: 800, marginBottom: '4px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Upload size={18} style={{ color: '#D4A017' }} /> Leads Uploaden
                </h3>
                <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', margin: 0 }}>
                  Upload CSV of Excel. Elke lead ontvangt automatisch een groeiscan email.
                </p>
              </div>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                <label data-testid="upload-leads-btn" style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: uploading ? 'wait' : 'pointer',
                  background: '#D4A017', color: '#0f172a', borderRadius: '8px', padding: '10px 18px',
                  fontSize: '13px', fontWeight: 700, opacity: uploading ? 0.6 : 1,
                }}>
                  <FileSpreadsheet size={15} />
                  {uploading ? 'Bezig...' : 'Upload CSV / Excel'}
                  <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" onChange={handleUpload} style={{ display: 'none' }} disabled={uploading} />
                </label>
                <button data-testid="url-import-btn" onClick={() => setShowUrlImport(!showUrlImport)} style={{
                  display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                  background: 'rgba(255,255,255,0.1)', color: 'white', borderRadius: '8px', padding: '10px 18px',
                  fontSize: '13px', fontWeight: 600, border: '1px solid rgba(255,255,255,0.15)',
                }}>
                  <Mail size={14} /> URL Import
                </button>
              </div>
            </div>

            {showUrlImport && (
              <div style={{ marginTop: '14px', display: 'flex', gap: '8px', background: 'rgba(255,255,255,0.06)', borderRadius: '8px', padding: '12px' }}>
                <input type="text" value={importUrl} onChange={e => setImportUrl(e.target.value)}
                  placeholder="Plak een directe link naar uw CSV bestand..."
                  style={{ flex: 1, background: 'rgba(255,255,255,0.08)', border: '1px solid rgba(255,255,255,0.15)', borderRadius: '6px', padding: '8px 12px', color: 'white', fontSize: '13px', outline: 'none' }} />
                <button onClick={handleUrlImport} disabled={uploading || !importUrl.trim()} style={{
                  background: '#D4A017', color: '#0f172a', borderRadius: '6px', padding: '8px 16px',
                  fontSize: '13px', fontWeight: 700, border: 'none', cursor: 'pointer',
                  opacity: (uploading || !importUrl.trim()) ? 0.5 : 1,
                }}>Importeren</button>
              </div>
            )}

            {uploadResult && (
              <div style={{ marginTop: '16px', background: 'rgba(255,255,255,0.08)', borderRadius: '8px', padding: '12px 16px', display: 'flex', gap: '20px' }}>
                <span style={{ fontSize: '12px' }}>Totaal: <b>{uploadResult.total}</b></span>
                <span style={{ fontSize: '12px', color: '#22c55e' }}>Opgeslagen: <b>{uploadResult.saved}</b></span>
                <span style={{ fontSize: '12px', color: '#d97706' }}>Overgeslagen: <b>{uploadResult.skipped}</b></span>
              </div>
            )}

            {batchProgress && (
              <div style={{ marginTop: '12px', background: 'rgba(255,255,255,0.06)', borderRadius: '10px', padding: '14px 18px' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <span style={{ fontSize: '12px', fontWeight: 700, color: '#D4A017' }}>
                    {batchProgress.status === 'complete' ? 'Voltooid' : 'Emails worden verstuurd...'}
                  </span>
                  <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)' }}>{batchProgress.sent + batchProgress.errors} / {batchProgress.total}</span>
                </div>
                <div style={{ background: 'rgba(255,255,255,0.1)', borderRadius: '6px', height: '8px', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: '6px',
                    background: batchProgress.status === 'complete' ? '#22c55e' : 'linear-gradient(90deg, #D4A017, #f59e0b)',
                    width: `${batchProgress.total > 0 ? Math.round(((batchProgress.sent + batchProgress.errors) / batchProgress.total) * 100) : 0}%`,
                  }} />
                </div>
              </div>
            )}
          </div>

          {/* Actions row */}
          <div style={{ display: 'flex', gap: '8px', marginBottom: '20px', flexWrap: 'wrap' }}>
            {(stats.pending || 0) > 0 && (
              <button data-testid="start-sending-btn" onClick={startSending} style={{
                display: 'inline-flex', alignItems: 'center', gap: '6px', cursor: 'pointer',
                background: '#22c55e', color: 'white', borderRadius: '10px', padding: '12px 20px',
                fontSize: '13px', fontWeight: 700, border: 'none',
              }}>
                <Mail size={15} /> Start verzending ({stats.pending} leads)
              </button>
            )}
            {(stats.errors || 0) > 0 && (
              <button onClick={resetErrors} style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer',
                background: '#fffbeb', color: '#d97706', borderRadius: '10px', padding: '12px 16px',
                fontSize: '13px', fontWeight: 600, border: '1px solid #fde68a',
              }}>
                <RefreshCw size={14} /> Reset {stats.errors} fouten
              </button>
            )}
            <button onClick={() => exportCSV('all')} style={{
              display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer',
              background: 'white', color: '#0f172a', borderRadius: '10px', padding: '12px 16px',
              fontSize: '13px', fontWeight: 600, border: '1px solid #e2e8f0',
            }}>
              <Download size={14} /> Export CSV
            </button>
            {stats.total > 0 && (
              <button onClick={clearAllLeads} style={{
                display: 'inline-flex', alignItems: 'center', gap: '5px', cursor: 'pointer',
                background: '#fef2f2', color: '#ef4444', borderRadius: '10px', padding: '12px 16px',
                fontSize: '13px', fontWeight: 600, border: '1px solid #fecaca',
              }}>
                <Trash2 size={14} /> Alles wissen
              </button>
            )}
          </div>

          {/* Strato Email Marketing */}
          {stats.total > 0 && (
            <div style={{
              background: '#f8fafc', borderRadius: '14px', padding: '20px', marginBottom: '20px',
              border: '1px solid #e2e8f0',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '10px' }}>
                <div>
                  <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                    <Mail size={15} style={{ color: '#D4A017' }} /> Strato Email Marketing
                  </h3>
                  <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>20.000 emails/maand via nieuwsbrief.imperiaai.nl</p>
                </div>
                <div style={{ display: 'flex', gap: '8px' }}>
                  <button onClick={() => exportCSV('pending')} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    background: '#D4A017', color: '#0f172a', borderRadius: '8px', padding: '8px 14px',
                    fontSize: '12px', fontWeight: 700, border: 'none', cursor: 'pointer',
                  }}>
                    <Download size={13} /> Export CSV
                  </button>
                  <button onClick={copyEmailTemplate} style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    background: 'white', color: '#0f172a', borderRadius: '8px', padding: '8px 14px',
                    fontSize: '12px', fontWeight: 600, border: '1px solid #e2e8f0', cursor: 'pointer',
                  }}>
                    <Copy size={13} /> Kopieer HTML
                  </button>
                  <a href="https://emt.strato.nl" target="_blank" rel="noopener noreferrer" style={{
                    display: 'inline-flex', alignItems: 'center', gap: '4px',
                    background: 'white', color: '#64748b', borderRadius: '8px', padding: '8px 14px',
                    fontSize: '12px', fontWeight: 600, border: '1px solid #e2e8f0', textDecoration: 'none',
                  }}>
                    <ExternalLink size={13} /> Open Strato
                  </a>
                </div>
              </div>
              <button onClick={() => setShowStratoGuide(!showStratoGuide)} style={{
                marginTop: '10px', background: 'none', border: 'none', color: '#D4A017',
                fontSize: '11px', fontWeight: 600, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px',
              }}>
                <ChevronDown size={13} style={{ transform: showStratoGuide ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                {showStratoGuide ? 'Verberg' : 'Bekijk'} instructies
              </button>
              {showStratoGuide && (
                <div style={{ marginTop: '10px', background: 'white', borderRadius: '10px', padding: '14px', fontSize: '12px', lineHeight: '1.8', color: '#374151' }}>
                  <ol style={{ margin: 0, paddingLeft: '18px' }}>
                    <li>Klik <b>"Export CSV"</b> om leads te downloaden</li>
                    <li>Open <a href="https://emt.strato.nl" target="_blank" rel="noopener noreferrer" style={{ color: '#D4A017' }}>Strato EMT</a> → Ontvangers → Importeren</li>
                    <li>Upload CSV (scheidingsteken: puntkomma)</li>
                    <li>Campagnes → Nieuwe campagne → Plak HTML template</li>
                    <li>Selecteer lijst → Verzenden</li>
                  </ol>
                </div>
              )}
            </div>
          )}
        </>
      )}

      {/* ── Tab: Tracking & Statistieken ── */}
      {tab === 'tracking' && ts && (
        <div>
          {/* Funnel stats */}
          <div className="tracking-grid-4" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '12px', marginBottom: '20px' }}>
            {[
              { label: 'Geopend', value: ts.totals.opened, sub: `${ts.rates.open_rate}% open rate`, color: '#818cf8', icon: Eye },
              { label: 'Geklikt', value: ts.totals.clicked, sub: `${ts.rates.click_rate}% click rate`, color: '#2dd4bf', icon: MousePointer },
              { label: 'Scan gestart', value: ts.click_details.scan_clicks, sub: 'Klik op groeiscan link', color: '#D4A017', icon: BarChart3 },
              { label: 'Geconverteerd', value: ts.totals.converted, sub: `${ts.rates.conversion_rate}% conversie`, color: '#22c55e', icon: Crown },
            ].map(s => {
              const Icon = s.icon;
              return (
                <div key={s.label} style={{
                  background: 'white', borderRadius: '14px', padding: '20px',
                  border: '1px solid #f1f5f9',
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
                    <div style={{ width: '36px', height: '36px', borderRadius: '10px', background: `${s.color}12`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                      <Icon size={18} style={{ color: s.color }} />
                    </div>
                  </div>
                  <p style={{ fontSize: '32px', fontWeight: 900, color: s.color, margin: '0 0 4px' }}>{s.value}</p>
                  <p style={{ fontSize: '12px', fontWeight: 700, color: '#0f172a', margin: '0 0 2px' }}>{s.label}</p>
                  <p style={{ fontSize: '11px', color: '#94a3b8', margin: 0 }}>{s.sub}</p>
                </div>
              );
            })}
          </div>

          {/* Conversie Funnel Visualization */}
          <div style={{ background: 'white', borderRadius: '14px', padding: '24px', border: '1px solid #f1f5f9', marginBottom: '20px' }}>
            <h3 style={{ fontSize: '14px', fontWeight: 700, color: '#0f172a', marginBottom: '16px' }}>Conversie Funnel</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              {[
                { label: 'Verstuurd', value: ts.totals.sent, color: '#334155', pct: 100 },
                { label: 'Geopend', value: ts.totals.opened, color: '#818cf8', pct: ts.totals.sent > 0 ? (ts.totals.opened / ts.totals.sent * 100) : 0 },
                { label: 'Geklikt', value: ts.totals.clicked, color: '#2dd4bf', pct: ts.totals.sent > 0 ? (ts.totals.clicked / ts.totals.sent * 100) : 0 },
                { label: 'Geconverteerd', value: ts.totals.converted, color: '#22c55e', pct: ts.totals.sent > 0 ? (ts.totals.converted / ts.totals.sent * 100) : 0 },
              ].map(s => (
                <div key={s.label} style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
                  <span style={{ width: '100px', fontSize: '12px', fontWeight: 600, color: '#64748b' }}>{s.label}</span>
                  <div style={{ flex: 1, height: '28px', background: '#f8fafc', borderRadius: '8px', overflow: 'hidden', position: 'relative' }}>
                    <div style={{
                      height: '100%', background: s.color, borderRadius: '8px',
                      width: `${Math.max(s.pct, 1)}%`, transition: 'width 0.5s ease',
                      display: 'flex', alignItems: 'center', paddingLeft: '10px',
                    }}>
                      {s.pct > 10 && <span style={{ fontSize: '11px', fontWeight: 700, color: 'white' }}>{s.value}</span>}
                    </div>
                    {s.pct <= 10 && <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', fontSize: '11px', fontWeight: 700, color: '#64748b' }}>{s.value}</span>}
                  </div>
                  <span style={{ width: '50px', textAlign: 'right', fontSize: '12px', fontWeight: 700, color: s.color }}>{s.pct.toFixed(1)}%</span>
                </div>
              ))}
            </div>
          </div>

          {/* Recent Activity */}
          <div className="recent-activity-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '16px' }}>
            <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #f1f5f9' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#818cf8', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Eye size={14} /> Laatst geopend
              </h3>
              {(ts.recent_opens || []).length === 0 ? (
                <p style={{ fontSize: '12px', color: '#94a3b8' }}>Nog geen opens geregistreerd</p>
              ) : ts.recent_opens.slice(0, 10).map((o, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a', margin: 0 }}>{o.bedrijfsnaam || o.email}</p>
                    <p style={{ fontSize: '10px', color: '#94a3b8', margin: 0 }}>{o.email}</p>
                  </div>
                  <span style={{ fontSize: '11px', color: '#818cf8', fontWeight: 600 }}>{o.open_count}x</span>
                </div>
              ))}
            </div>

            <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #f1f5f9' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#2dd4bf', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <MousePointer size={14} /> Laatst geklikt
              </h3>
              {(ts.recent_clicks || []).length === 0 ? (
                <p style={{ fontSize: '12px', color: '#94a3b8' }}>Nog geen kliks geregistreerd</p>
              ) : ts.recent_clicks.slice(0, 10).map((c, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a', margin: 0 }}>{c.bedrijfsnaam || c.email}</p>
                    <p style={{ fontSize: '10px', color: '#94a3b8', margin: 0 }}>
                      {(c.clicks || []).slice(-1).map(cl => cl.type === 'scan' ? 'Groeiscan link' : cl.type === 'beheer' ? 'IMPERIA AI Beheer' : cl.type).join('')}
                    </p>
                  </div>
                  <span style={{ fontSize: '11px', color: '#2dd4bf', fontWeight: 600 }}>{c.click_count}x</span>
                </div>
              ))}
            </div>

            <div style={{ background: 'white', borderRadius: '14px', padding: '20px', border: '1px solid #f1f5f9' }}>
              <h3 style={{ fontSize: '13px', fontWeight: 700, color: '#22c55e', marginBottom: '12px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                <Crown size={14} /> Recente conversies
              </h3>
              {(ts.recent_conversions || []).length === 0 ? (
                <p style={{ fontSize: '12px', color: '#94a3b8' }}>Nog geen conversies geregistreerd</p>
              ) : ts.recent_conversions.slice(0, 10).map((c, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #f8fafc' }}>
                  <div>
                    <p style={{ fontSize: '12px', fontWeight: 600, color: '#0f172a', margin: 0 }}>{c.bedrijfsnaam || c.email}</p>
                    <p style={{ fontSize: '10px', color: '#94a3b8', margin: 0 }}>{c.email}</p>
                  </div>
                  <span style={{ fontSize: '10px', color: '#22c55e', fontWeight: 600 }}>
                    {c.converted_at ? new Date(c.converted_at).toLocaleDateString('nl-NL') : '—'}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Tab: Alle Leads ── */}
      {tab === 'tabel' && (
        <div style={{ background: 'white', borderRadius: '14px', border: '1px solid #f1f5f9', overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px', minWidth: '700px' }}>
            <thead>
              <tr style={{ borderBottom: '2px solid #f1f5f9' }}>
                {['Email', 'Bedrijf', 'Contact', 'Status', 'Geopend', 'Geklikt', 'Datum', ''].map(h => (
                  <th key={h} style={{ padding: '12px', textAlign: 'left', fontSize: '10px', color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Laden...</td></tr>
              ) : leads.length === 0 ? (
                <tr><td colSpan={8} style={{ padding: '40px', textAlign: 'center', color: '#94a3b8' }}>Nog geen leads.</td></tr>
              ) : leads.map(lead => {
                const sc = SC[lead.status] || SC.pending;
                const Icon = sc.icon;
                return (
                  <tr key={lead.id} style={{ borderBottom: '1px solid #f8fafc' }}>
                    <td style={{ padding: '10px 12px', fontWeight: 600, color: '#0f172a', maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>{lead.email}</td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{lead.bedrijfsnaam || '—'}</td>
                    <td style={{ padding: '10px 12px', color: '#374151' }}>{lead.contact_naam || '—'}</td>
                    <td style={{ padding: '10px 12px' }}>
                      <span style={{
                        display: 'inline-flex', alignItems: 'center', gap: '4px',
                        fontSize: '11px', fontWeight: 600, color: sc.color,
                        background: `${sc.color}12`, padding: '2px 8px', borderRadius: '6px',
                      }}>
                        <Icon size={11} /> {sc.label}
                      </span>
                    </td>
                    <td style={{ padding: '10px 12px', color: lead.open_count ? '#818cf8' : '#d1d5db', fontWeight: 600, fontSize: '11px' }}>
                      {lead.open_count ? `${lead.open_count}x` : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: lead.click_count ? '#2dd4bf' : '#d1d5db', fontWeight: 600, fontSize: '11px' }}>
                      {lead.click_count ? `${lead.click_count}x` : '—'}
                    </td>
                    <td style={{ padding: '10px 12px', color: '#94a3b8', fontSize: '11px' }}>
                      {lead.uploaded_at ? new Date(lead.uploaded_at).toLocaleDateString('nl-NL') : '—'}
                    </td>
                    <td style={{ padding: '10px 12px' }}>
                      {lead.status !== 'converted' && (
                        <button onClick={() => resendEmail(lead.id)} title="Opnieuw versturen"
                          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '4px' }}>
                          <RotateCcw size={13} style={{ color: '#64748b' }} />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
